import { motion } from 'framer-motion';
import { LegacyRef, useState } from 'react';
import { BsFillSignStopFill, BsThreeDotsVertical } from 'react-icons/bs';
import { FaBan, FaUser, FaUserSlash } from 'react-icons/fa';
import { FaTrashCan } from 'react-icons/fa6';
import { MdPassword } from 'react-icons/md';
import { RiEditFill } from 'react-icons/ri';
import { useLocation } from 'react-router-dom';
import { useMutationModalContext } from '../../contexts/MutationModalProvider';
import useFeatureBasedPermissions from '../../hooks/useFeatureBasedPermissions';
import useUserClickObserver from '../../hooks/useUserClickObserver';
import { Associate } from '../../types/associates';
import { FeatureName } from '../../types/features';
import ActionButton from './Buttons/ActionButton';

const CommonUpdateAction = ({
  updateBtnLabel,
  deleteBtnLabel,
  featureName,
  deactivateBtnLabel,
  rejectBtnLabel,
  rowIndex,
  data,
  isUsersPage,
}: {
  updateBtnLabel: string;
  deleteBtnLabel: string;
  featureName: FeatureName;
  deactivateBtnLabel?: string;
  rejectBtnLabel?: string;
  rowIndex?: number;
  data?: unknown;
  isUsersPage?: boolean;
}) => {
  // integration of react hooks here
  const [showMenu, setShowMenu] = useState<boolean>(false);

  // integration of context hooks here
  const {
    setData,
    setShowUpdateModal,
    setShowUpdatePermissionModal,
    setShowDeleteModal,
    setShowPasswordModal,
    setShowDeactivateModal,
    setShowRejectModal,
  } = useMutationModalContext();

  // integration of custom hooks here
  const selectRef = useUserClickObserver(setShowMenu);
  const { featureBasedPermissions } = useFeatureBasedPermissions(featureName);

  // integration of react-router-dom hooks here
  const { pathname } = useLocation();

  // handler function to show modal on button click declared here
  const showModalHandler = (
    modalName:
      | 'update'
      | 'permissions'
      | 'delete'
      | 'change'
      | 'deactivate'
      | 'reject'
  ) => {
    switch (modalName) {
      case 'update':
        setShowUpdateModal(true);
        setData(typeof rowIndex !== 'undefined' ? { data, rowIndex } : data);
        break;

      case 'permissions':
        setShowUpdatePermissionModal(true);
        setData(typeof rowIndex !== 'undefined' ? { data, rowIndex } : data);
        break;

      case 'delete':
        setShowDeleteModal(true);
        setData(typeof rowIndex !== 'undefined' ? { data, rowIndex } : data);
        break;

      case 'change':
        setShowPasswordModal(true);
        setData(data);
        break;

      case 'deactivate':
        setShowDeactivateModal(true);
        setData(data);
        break;

      case 'reject':
        setShowRejectModal(true);
        setData(data);
        break;

      default:
        break;
    }

    setShowMenu(false);
  };

  // rendering update action wrapper component here
  return (
    <div ref={selectRef as LegacyRef<HTMLDivElement>} className='relative'>
      <div className='flex items-center justify-center'>
        <button
          onClick={() => setShowMenu((state) => !state)}
          className='hover:opacity-40 active:scale-90 duration-300'
        >
          <BsThreeDotsVertical className='size-6' />
        </button>
      </div>

      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={
          showMenu
            ? { height: 'fit-content', opacity: 1 }
            : { height: 0, opacity: 0 }
        }
        className='absolute bottom-1/2 right-0 bg-base-white shadow-academia-sm rounded-lg overflow-hidden '
      >
        <div className='max-h-[100px] overflow-y-auto academia-scrollbar'>
          <ActionButton
            title={
              !featureBasedPermissions?.includes('UPDATE')
                ? 'You do not have permission'
                : undefined
            }
            onClick={() => showModalHandler('update')}
            colorClass='text-base-cyan'
            isDisabled={!featureBasedPermissions?.includes('UPDATE')}
          >
            <RiEditFill />
            <span>{updateBtnLabel}</span>
          </ActionButton>

          {isUsersPage && pathname?.includes('associate') ? (
            <ActionButton
              title={
                !featureBasedPermissions?.includes('UPDATE')
                  ? 'You do not have permission'
                  : undefined
              }
              onClick={() => showModalHandler('permissions')}
              colorClass='text-base-light-blue'
              isDisabled={!featureBasedPermissions?.includes('UPDATE')}
            >
              <BsFillSignStopFill />
              <span>Update Permissions</span>
            </ActionButton>
          ) : null}

          {isUsersPage ? (
            <ActionButton
              title={
                !featureBasedPermissions?.includes('UPDATE')
                  ? 'You do not have permission'
                  : undefined
              }
              onClick={() => showModalHandler('change')}
              colorClass='text-base-purple'
              isDisabled={!featureBasedPermissions?.includes('UPDATE')}
            >
              <MdPassword />
              <span>Change Password</span>
            </ActionButton>
          ) : null}

          {rejectBtnLabel ? (
            <ActionButton
              onClick={() => showModalHandler('reject')}
              colorClass='text-base-red'
            >
              <FaBan />
              <span>{rejectBtnLabel}</span>
            </ActionButton>
          ) : null}

          <ActionButton
            title={
              !featureBasedPermissions?.includes('DELETE')
                ? 'You do not have permission'
                : undefined
            }
            onClick={() => showModalHandler('delete')}
            colorClass='text-base-red'
            isDisabled={!featureBasedPermissions?.includes('DELETE')}
          >
            <FaTrashCan />
            <span>{deleteBtnLabel}</span>
          </ActionButton>

          {deactivateBtnLabel ? (
            <ActionButton
              title={
                !featureBasedPermissions?.includes('UPDATE')
                  ? 'You do not have permission'
                  : undefined
              }
              onClick={() => showModalHandler('deactivate')}
              colorClass={`${
                (data as Associate).is_active
                  ? 'text-base-red'
                  : 'text-base-cyan'
              }  `}
              isDisabled={!featureBasedPermissions?.includes('UPDATE')}
            >
              {(data as Associate).is_active ? (
                <>
                  <FaUserSlash />
                  <span className='capitalize'>
                    deactivate {deactivateBtnLabel}
                  </span>
                </>
              ) : (
                <>
                  <FaUser />
                  <span>Activate {deactivateBtnLabel}</span>
                </>
              )}
            </ActionButton>
          ) : null}
        </div>
      </motion.div>
    </div>
  );
};

export default CommonUpdateAction;
